import {
  ConformiteGarantieFinanciereEditFormIas,
  useConformiteGarantieFinanciereIasEdit,
} from "@src/conformite/garantieFinanciere/edit/ConformiteGarantieFinanciereEditFormIas";
import { useMillesime } from "@src/store/store.millesime";
import { useCallback, useEffect, useState } from "react";
import { GatewayGarantieFinanciere } from "@conformite/gateway";
import { GatewayGarantieFinanciereApi } from "@src/api/conformite/garantieFinanciere.api";
import { handleGatewayError } from "@src/api/utils/handleGatewayError";
import { displayErrorToast } from "@endya-conformite/ui-kit";
import { useEntreprises } from "@src/store/store.entreprise";
import {
  ParcoursConformiteStepComponentProps,
  useParcoursConformite,
} from "../../ParcoursConformite";
import { ParcoursConformiteTemplate } from "../../ParcoursConformiteTemplate";
import { ParcoursConformiteLoaderStep } from "../../ParcoursConformiteLoaderStep";

function FormContainer({
  siren,
  changeStep,
  currentStep,
  millesime,
  garantieFinanciere,
}: {
  millesime: number;
  garantieFinanciere: GatewayGarantieFinanciere.ConformiteGarantieFinanciereIas | null;
} & ParcoursConformiteStepComponentProps) {
  const { entreprise } = useParcoursConformite();
  const { updateAvancement } = useEntreprises();

  const { formConfig, onSubmit } = useConformiteGarantieFinanciereIasEdit(
    siren,
    millesime,
    garantieFinanciere,
    (_, avancement) => updateAvancement(avancement)
  );

  const handleSubmit = useCallback(async () => {
    if (formConfig.formState.isDirty) await onSubmit(formConfig.getValues());
  }, [formConfig, onSubmit]);

  return (
    <ParcoursConformiteTemplate
      currentStep={currentStep}
      changeStep={changeStep}
      isConforme={entreprise?.avancement.garantieFinanciereIas === 100}
      isDirty={formConfig.formState.isDirty}
      size="large"
      title="Garantie financière IAS"
      onSubmit={handleSubmit}
    >
      <ConformiteGarantieFinanciereEditFormIas
        siren={siren}
        millesime={millesime}
        garantieFinanciere={garantieFinanciere}
        formConfig={formConfig}
      />
    </ParcoursConformiteTemplate>
  );
}

export function ConformiteGarantieFinanciereIasStep({
  siren,
  changeStep,
  currentStep,
  ...props
}: ParcoursConformiteStepComponentProps) {
  const { selected: millesime } = useMillesime();

  const [garantieFinanciere, setGarantieFinanciere] =
    useState<GatewayGarantieFinanciere.ConformiteGarantieFinanciereIas | null>();
  useEffect(() => {
    if (!siren || !millesime) return;
    GatewayGarantieFinanciereApi.getGarantieFinanciereIas(siren, millesime)
      .then((data) => {
        setGarantieFinanciere(data);
      })
      .catch((error) => {
        handleGatewayError({
          onUnhandled: () => {
            displayErrorToast(
              "Une erreur est survenue lors de la récupération du formulaire"
            );
          },
        })(error);
      });
  }, [siren, millesime]);

  if (!millesime || garantieFinanciere === undefined)
    return (
      <ParcoursConformiteLoaderStep
        currentStep={currentStep}
        changeStep={changeStep}
        title="Garantie financière IAS"
        size="large"
      />
    );

  return (
    <FormContainer
      {...props}
      siren={siren}
      changeStep={changeStep}
      currentStep={currentStep}
      garantieFinanciere={garantieFinanciere}
      millesime={millesime}
    />
  );
}
